/**
 * Secure token storage utility
 * Provides secure alternatives to localStorage for sensitive data
 */

import { authLogger } from './secure-logger';

interface StorageOptions {
  encrypt?: boolean;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  maxAge?: number; // in seconds
}

/**
 * Simple encryption/decryption using Web Crypto API
 * Note: This is client-side encryption for obfuscation only
 * Real security should use httpOnly cookies
 */
class ClientSideEncryption {
  private static async getKey(): Promise<CryptoKey> {
    // Use a deterministic key based on browser fingerprint
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      new Date().getTimezoneOffset()
    ].join('|');
    
    const encoder = new TextEncoder();
    const data = encoder.encode(fingerprint);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    return crypto.subtle.importKey(
      'raw',
      hashBuffer.slice(0, 32),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  static async encrypt(text: string): Promise<string> {
    try {
      const key = await this.getKey();
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      
      const encrypted = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        data
      );
      
      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);
      
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      return text;
    }
  }

  static async decrypt(encryptedText: string): Promise<string> {
    try {
      const key = await this.getKey();
      const combined = new Uint8Array(
        atob(encryptedText).split('').map(char => char.charCodeAt(0))
      );
      
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);
      
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted
      );
      
      const decoder = new TextDecoder();
      return decoder.decode(decrypted);
    } catch (error) {
      return encryptedText;
    }
  }
}

/**
 * Secure storage manager
 */
class SecureStorage {
  private static readonly TOKEN_KEY = 'ld_auth_token';
  private static readonly REFRESH_TOKEN_KEY = 'ld_refresh_token';
  private static readonly STORAGE_PREFIX = 'ld_secure_';

  /**
   * Set a secure cookie (preferred method)
   */
  private static setCookie(
    name: string,
    value: string,
    options: StorageOptions = {}
  ): void {
    const {
      secure = location.protocol === 'https:',
      sameSite = 'lax', // Changed from 'strict' to 'lax' to support PWA cookie persistence on mobile
      maxAge = 3600, // 1 hour default
      httpOnly = false
    } = options;

    let cookieString = `${name}=${value}; path=/; max-age=${maxAge}; samesite=${sameSite}`;
    
    if (secure) {
      cookieString += '; secure';
    }
    
    if (httpOnly) {
      // Note: httpOnly cannot be set from client-side JavaScript
      // This would need to be set by the server
      authLogger.warn('httpOnly cookies must be set by server');
    }

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value
   */
  private static getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Delete a cookie
   */
  private static deleteCookie(name: string): void {
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
  }

  /**
   * Store token securely
   */
  static async setToken(token: string, options: StorageOptions = {}): Promise<void> {
    try {
      if (options.httpOnly) {
        // For httpOnly cookies, we'd need server-side support
        authLogger.warn('httpOnly token storage requires server-side implementation');
        return;
      }

      const encryptedToken = options.encrypt ? await ClientSideEncryption.encrypt(token) : token;
      
      // Try to use secure cookie first
      if (document.cookie !== undefined) {
        this.setCookie(this.TOKEN_KEY, encryptedToken, {
          ...options,
          maxAge: options.maxAge || 28800 // 8 hours
        });
      } else {
        // Fallback to sessionStorage (more secure than localStorage)
        sessionStorage.setItem(this.STORAGE_PREFIX + this.TOKEN_KEY, encryptedToken);
      }
      
      authLogger.auth('Token stored securely');
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get token securely
   */
  static async getToken(): Promise<string | null> {
    try {
      let encryptedToken: string | null = null;
      
      // Try cookie first
      encryptedToken = this.getCookie(this.TOKEN_KEY);
      
      // Fallback to sessionStorage
      if (!encryptedToken) {
        encryptedToken = sessionStorage.getItem(this.STORAGE_PREFIX + this.TOKEN_KEY);
      }
      
      if (!encryptedToken) {
        return null;
      }
      
      // Try to decrypt if it looks encrypted
      if (encryptedToken.length > 100 && encryptedToken.includes('=')) {
        try {
          return await ClientSideEncryption.decrypt(encryptedToken);
        } catch {
          return encryptedToken; // Return as-is if decryption fails
        }
      }
      
      return encryptedToken;
    } catch (error) {
      return null;
    }
  }

  /**
   * Store refresh token securely
   */
  static async setRefreshToken(token: string, options: StorageOptions = {}): Promise<void> {
    try {
      const encryptedToken = options.encrypt ? await ClientSideEncryption.encrypt(token) : token;
      
      // Refresh tokens should have longer expiry
      this.setCookie(this.REFRESH_TOKEN_KEY, encryptedToken, {
        ...options,
        maxAge: options.maxAge || 604800 // 7 days
      });
      
      authLogger.auth('Refresh token stored securely');
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get refresh token securely
   */
  static async getRefreshToken(): Promise<string | null> {
    try {
      let encryptedToken = this.getCookie(this.REFRESH_TOKEN_KEY);
      
      if (!encryptedToken) {
        return null;
      }
      
      // Try to decrypt if it looks encrypted
      if (encryptedToken.length > 100 && encryptedToken.includes('=')) {
        try {
          return await ClientSideEncryption.decrypt(encryptedToken);
        } catch {
          return encryptedToken;
        }
      }
      
      return encryptedToken;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear all tokens
   */
  static clearTokens(): void {
    try {
      // Clear cookies
      this.deleteCookie(this.TOKEN_KEY);
      this.deleteCookie(this.REFRESH_TOKEN_KEY);
      
      // Clear sessionStorage
      sessionStorage.removeItem(this.STORAGE_PREFIX + this.TOKEN_KEY);
      sessionStorage.removeItem(this.STORAGE_PREFIX + this.REFRESH_TOKEN_KEY);
      
      // Clear localStorage (legacy cleanup)
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      authLogger.auth('All tokens cleared');
    } catch (error) {
    }
  }

  /**
   * Check if tokens exist
   */
  static async hasValidTokens(): Promise<boolean> {
    const token = await this.getToken();
    return token !== null && token.length > 0;
  }
}

export default SecureStorage;
